import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }

  interface SimplePaletteColorOptions {
    '-1'?: string;
    '+1'?: string;
    '+2'?: string;
    '+3'?: string;
    '+4'?: string;
  }

  interface PaletteColor {
    lighter?: string;
    darker?: string;
    '-1'?: string;
    '+1'?: string;
    '+2'?: string;
    '+3'?: string;
    '+4'?: string;
  }

  interface Palette {
    custom: typeof CUSTOM;
    neutral: typeof NEUTRAL;
    tetriary: typeof TETRIARY;
  }

  interface PaletteOptions {
    custom: typeof CUSTOM;
    neutral: typeof NEUTRAL;
    tetriary: typeof TETRIARY;
  }
}

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const NEUTRAL = {
  900: '#1B232C',
  800: '#4E5A65',
  700: '#637381',
  600: '#7D8D9B',
  500: '#9DA7B2',
  400: '#C5CCD3',
  300: '#E8EBEE',
  200: '#F6F7F8',
  100: '#FFFFFF',
};

const TETRIARY = {
  dark: '#00849C',
  '-1': '#00849C',
  '+1': '#AAE7F2',
  '+2': '#E9F9FC',
  main: '#00B8D9',
  light: '#AAE7F2',
  contrastText: '#FFFFFF',
};
const PRIMARY = {
  '-1': '#0D41E1',
  main: '#0C63E7',
  '+1': '#AFC8ED',
  '+2': '#BDD7FF',
  '+3': '#DDEBFF',
  '+4': '#EBF3FF',
};

const SECONDARY = {
  '-1': '#008F63',
  '+1': '#AAEDCD',
  '+2': '#EAFBF3',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  contrastText: '#FFFFFF',
};

const INFO = {
  light: PRIMARY['+1'],
  main: PRIMARY.main,
  dark: PRIMARY['-1'],
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  light: '#86E8AB',
  main: '#36B37E',
  dark: '#1B806A',
  contrastText: '#FFFFFF',
};

const WARNING = {
  '-1': '#E86C00',
  '+1': '#FCDFA7',
  '+2': '#FEEFC9',
  light: '#FCDFA7',
  main: '#FD8900',
  dark: '#E86C00',
  contrastText: '#1B232C',
};

const ERROR = {
  '-1': '#CC2600',
  '+1': '#FFD3C9',
  light: '#FFD3C9',
  main: '#FF3000',
  dark: '#B71D18',
  contrastText: '#FFFFFF',
};

// SETUP COLORS
const CUSTOM = {
  red: ERROR.main,
  blue: PRIMARY.main,
  grey: NEUTRAL[300],
  green: '#00A76B',
  orange: WARNING['-1'],
};

const COMMON = {
  custom: CUSTOM,
  neutral: NEUTRAL,
  tetriary: TETRIARY,
  common: { black: '#000000', white: '#FFFFFF' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: NEUTRAL[300],
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const light = {
  ...COMMON,
  mode: 'light',
  text: {
    primary: NEUTRAL[900],
    secondary: GREY[600],
    disabled: GREY[500],
  },
  background: {
    paper: NEUTRAL[100],
    default: NEUTRAL[100],
    neutral: GREY[200],
  },
  action: {
    ...COMMON.action,
    active: GREY[600],
  },
} as const;

export const getPalette = (_: 'light' | 'dark') => light;
